import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { useSession, assetUrl } from "@/contexts/SessionContext";
import { Trophy, Award, Lock } from "lucide-react";
import { SnapshotsPanel } from "@/components/ForensicLab";
import { LeaderboardPanel } from "@/components/Leaderboard";
import { useAuth } from "@/pages/AuthPage";
import { toast } from "sonner";

export function CasesList() {
  const [cases, setCases] = useState([]);
  const [caseRelease, setCaseRelease] = useState(null);   // null = still loading
  const { user, isOwner } = useAuth();
  useEffect(() => { api.get("/cases?include_locked=true").then(r => setCases(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get("/case-release").then(r => setCaseRelease(r.data || {})).catch(() => setCaseRelease({})); }, []);
  const allowed = useMemo(() => {
    if (!user) return null;
    if (isOwner) return cases;
    if (caseRelease === null) return null;
    const assigned = user.assigned_cases;
    const bySet = (assigned === "all" || assigned == null) ? cases
      : cases.filter((c) => (Array.isArray(assigned) ? assigned.includes(c.id) : false));
    return bySet.filter((c) => {
      const cr = caseRelease[c.id] || {};
      return cr.status !== "HIDDEN";
    });
  }, [cases, user, isOwner, caseRelease]);
  return (
    <Shell>
      <h1 className="font-cairo text-3xl font-black text-white mb-6">جميع القضايا</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {allowed === null ? (
          <>{[0,1,2].map(i => (
            <div key={i} className="panel h-40 animate-pulse bg-zinc-900/40 border border-zinc-800/60" />
          ))}</>
        ) : allowed.length === 0 ? (
          <div className="col-span-3 text-center text-zinc-500 py-12 font-cairo" data-testid="caseslist-empty">
            لا توجد قضايا متاحة لك حالياً
          </div>
        ) : (
          allowed.map(c => {
            const rel = caseRelease?.[c.id] || {};
            const isLocked = Boolean(c.is_locked || rel.is_locked || rel.status === "UPCOMING_LOCKED" || (rel.available === false && rel.status !== "HIDDEN"));
            return (
              <Link
                key={c.id}
                to={isLocked && !isOwner ? "#" : `/cases/${c.id}`}
                onClick={(e) => {
                  if (isLocked && !isOwner) {
                    e.preventDefault();
                    e.stopPropagation();
                    toast.info("هذه القضية مقفلة حالياً — ستكون متاحة قريباً!", { icon: "🔒" });
                  }
                }}
                data-testid={`caseslist-${c.id}`}
                className={`panel overflow-hidden hover:border-gold transition-all group relative block ${isLocked ? "border-gold/40 hover:border-gold/70" : ""}`}
              >
                <div className="relative h-44 bg-zinc-950">
                  <img
                    src={assetUrl(c.cover || c.scene) || "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=1200&h=800&fit=crop"}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    alt={c.title}
                    onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1522798514-97ceb8c4f1c8?w=1200&h=800&fit=crop"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                  {isLocked && (
                    <div className="absolute top-3 left-3 bg-black/90 border border-gold text-gold text-[10px] px-2.5 py-0.5 font-cairo font-bold flex items-center gap-1 shadow-[0_0_12px_rgba(217,166,80,0.35)]">
                      <Lock size={11} className="text-gold" />
                      <span>قريباً</span>
                    </div>
                  )}
                  {isLocked && (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-black/80 border border-gold/60 backdrop-blur flex flex-col items-center justify-center">
                      <Lock size={22} className="text-gold" />
                      <span className="text-[9px] font-cairo text-gold font-bold">قريباً</span>
                    </div>
                  )}
                  <div className="absolute bottom-3 right-3 left-3">
                    <div className="font-cairo text-xl font-bold text-white leading-tight mb-0.5">{c.title}</div>
                    <div className="text-xs text-gold font-mono-ar">CASE {c.code}</div>
                  </div>
                </div>
              </Link>
            );
          })
        )}
      </div>
    </Shell>
  );
}

export function Leaderboard() {
  return (
    <Shell>
      <div className="flex items-center gap-3 mb-6">
        <Trophy size={28} className="text-gold" />
        <h1 className="font-cairo text-3xl font-black text-white">ترتيب المحققين</h1>
        <span className="text-[10px] text-zinc-500 font-mono-ar mr-3">SESSION LEADERBOARD · مسجّل حسب التصويت الصحيح للمشتبه به الحقيقي</span>
      </div>
      <div className="max-w-3xl">
        <LeaderboardPanel />
      </div>
    </Shell>
  );
}

export function Credits() {
  const year = new Date().getFullYear();
  return (
    <Shell>
      <div className="max-w-3xl mx-auto" data-testid="credits-page">
        <div className="text-center mb-8">
          <div className="text-[11px] tracking-[0.4em] text-gold font-mono-ar mb-2">ABOUT · حول اللعبة</div>
          <h1 className="font-cairo text-4xl font-black text-white mb-2">OK FBI Investigation Game</h1>
          <div className="text-sm text-zinc-400">لعبة تحقيق تفاعلية سينمائية باللغة العربية للبث المباشر</div>
        </div>
        <div className="panel gold-border p-8 space-y-4">
          <CreditRow k="فكرة وتطوير" v="OK" />
          <CreditRow k="تصميم الواجهة" v="OK" />
          <CreditRow k="برمجة النظام" v="OK" />
          <CreditRow k="تصميم تجربة التحقيق" v="OK" />
          <CreditRow k="تطوير نظام القضايا" v="OK" />
          <CreditRow k="التصميم البصري والصور السينمائية" v="OK" />
          <div className="border-t border-metal pt-4 mt-4 text-center">
            <div className="text-gold font-cairo text-lg tracking-[0.3em]">AN OK ORIGINAL</div>
            <div className="text-xs text-zinc-500 mt-2 font-cairo">CREATED BY // AMW</div>
            <div className="text-[10px] text-zinc-600 mt-1">OK FBI INVESTIGATION GAME · ALL RIGHTS RESERVED {year} ©</div>
          </div>
        </div>
        <div className="mt-6 text-center text-[10px] text-zinc-600 tracking-widest font-mono-ar">DESIGNED & DEVELOPED BY OK</div>
      </div>
    </Shell>
  );
}

function CreditRow({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-metal/50 pb-3">
      <span className="text-zinc-400 font-cairo text-sm">{k}</span>
      <span className="font-cairo text-xl font-black text-gold tracking-widest">{v}</span>
    </div>
  );
}

export function StubPage({ title }) {
  return <Shell><h1 className="font-cairo text-3xl font-black text-white">{title}</h1>
    <div className="mt-4 text-zinc-500">هذه العلامة ستُعرض عند اختيار قضية محددة. اذهب إلى <Link to="/cases" className="text-gold">قائمة القضايا</Link>.</div>
  </Shell>;
}

export function SettingsPage() {
  const { session, updateSession, resetSession } = useSession();
  const { user, isOwner } = useAuth();
  const plan = String(user?.membership_plan || "").toUpperCase();
  const role = String(user?.role || "").toUpperCase();
  const canControlSession = isOwner || plan === "STREAMER" || role === "STREAMER";
  const sessionPermissionMessage = "إعدادات الجلسة متاحة للستريمر أو المالك فقط";
  const rejectSessionControl = () => toast.error(sessionPermissionMessage);
  return (
    <Shell>
      <h1 className="font-cairo text-3xl font-black text-white mb-6">الإعدادات</h1>
      <div className="max-w-2xl space-y-3">
        <div className="panel p-4 flex justify-between items-center border-metal">
          <div>
            <span className="text-white">وضع البث (Stream Mode)</span>
            {!canControlSession && <div className="text-[10px] text-amber-300/80 font-cairo mt-1">متاح للستريمر أو المالك فقط</div>}
          </div>
          <button onClick={() => canControlSession ? updateSession({ stream_mode: !session?.stream_mode }) : rejectSessionControl()} data-testid="settings-stream-toggle"
            disabled={!canControlSession} title={!canControlSession ? sessionPermissionMessage : "تبديل وضع البث"}
            className={`w-14 h-7 rounded-full ${session?.stream_mode ? "bg-red-800" : "bg-zinc-800"} relative disabled:opacity-40 disabled:cursor-not-allowed`}>
            <div className={`w-6 h-6 bg-white rounded-full absolute top-0.5 transition-all ${session?.stream_mode ? "right-0.5" : "left-0.5"}`} />
          </button>
        </div>
        <div className="panel p-4 border-metal">
          <div className="text-white mb-2">إعادة تعيين الجلسة</div>
          <div className="text-xs text-zinc-500 mb-3">يمسح تقدم كل القضايا والتصويت والملاحظات (يحتفظ بالمشاركين).</div>
          <button onClick={() => canControlSession ? resetSession() : rejectSessionControl()} data-testid="settings-reset"
            disabled={!canControlSession} title={!canControlSession ? sessionPermissionMessage : "إعادة تعيين الجلسة"}
            className="btn-ghost text-sm text-red-400 border-red-900/50 disabled:opacity-40 disabled:cursor-not-allowed">إعادة تعيين</button>
          {!canControlSession && <div className="text-[10px] text-amber-300/80 font-cairo">إعادة التعيين متاحة للستريمر أو المالك فقط</div>}
        </div>
        <SnapshotsPanel canManage={canControlSession} />
      </div>
    </Shell>
  );
}
